import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { User } from '@supabase/supabase-js';
import { useAuth } from './hooks/useAuth';
import Auth from './components/Auth';
import Header from './components/Header';
import Dashboard from './components/Dashboard';
import ManageRoles from './components/admin/ManageRoles';
import ManageJury from './components/admin/ManageJury';
import PlatformConfiguration from './components/admin/PlatformConfiguration';
import NominationForm from './components/nominations/NominationForm';
import JuryVotes from './components/dashboards/JuryVotes';
import PublicVoting from './components/dashboards/PublicVoting';
import EditProfile from './components/EditProfile';
import ResetPassword from './components/ResetPassword';
import AuthCallback from './pages/AuthCallback';
import Landing from './components/landing/Landing';
import CandidaturasWrapped from './pages/CandidaturasWrapped';
import InformeCandidaturas from './pages/InformeCandidaturas';
import ValoracionJurado from './pages/ValoracionJurado';
import FinalistFrame from './pages/FinalistFrame';
import FinalTwo from './pages/FinalTwo';
import GalaRegistration from './pages/GalaRegistration';
import ManageGalaRegistrations from './components/admin/ManageGalaRegistrations';
import './App.css';

const App: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <Router>
      <AppShell user={user} />
    </Router>
  );
};

// Página con su propia cabecera compacta; la cabecera global del sitio no aporta aquí.
const HIDE_GLOBAL_HEADER_ON = ['/informe-candidaturas', '/valoracion-jurado', '/soy-finalista', '/finalistas-2026', '/inscripcion-gala'];

const AppShell: React.FC<{ user: User | null }> = ({ user }) => {
  const location = useLocation();
  const hideGlobalHeader = HIDE_GLOBAL_HEADER_ON.includes(location.pathname);

  return (
    <div className="app">
      {!hideGlobalHeader && <Header />}
      <Routes>
        {/* Candidaturas requieren autenticación */}
        <Route
          path="/nominate"
          element={user ? <NominationForm /> : <Navigate to="/auth" />}
        />
        <Route
          path="/nominate/:id"
          element={user ? <NominationForm /> : <Navigate to="/auth" />}
        />

        {/* Rutas que requieren autenticación */}
        <Route
          path="/dashboard"
          element={user ? <Dashboard /> : <Navigate to="/auth" />}
        />
        <Route
          path="/manage-roles"
          element={user ? <ManageRoles /> : <Navigate to="/auth" />}
        />
        <Route
          path="/manage-jury"
          element={user ? <ManageJury /> : <Navigate to="/auth" />}
        />
        <Route
          path="/config"
          element={user ? <PlatformConfiguration /> : <Navigate to="/auth" />}
        />
        <Route
          path="/votaciones"
          element={user ? <JuryVotes /> : <Navigate to="/auth" />}
        />
        <Route
          path="/votar"
          element={user ? <PublicVoting /> : <Navigate to="/auth" />}
        />
        <Route
          path="/profile"
          element={user ? <EditProfile /> : <Navigate to="/auth" />}
        />

        <Route path="/auth" element={!user ? <Auth /> : <Navigate to="/dashboard" />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Resumen público de candidaturas (experiencia tipo "wrapped") */}
        <Route path="/candidaturas" element={<CandidaturasWrapped />} />

        {/* Informe completo de candidaturas, con índice de navegación por categoría */}
        <Route path="/informe-candidaturas" element={<InformeCandidaturas />} />

        {/* Valoración pública del jurado: gráficas de puntuación + comentarios por candidatura */}
        <Route path="/valoracion-jurado" element={<ValoracionJurado />} />

        {/* Generador de imagen para compartir, pensado para finalistas */}
        <Route path="/soy-finalista" element={<FinalistFrame />} />

        {/* Anuncio de los 2 últimos finalistas por categoría (sin revelar orden) */}
        <Route path="/finalistas-2026" element={<FinalTwo />} />

        {/* Inscripción a la Gala — enlace oculto, no enlazado desde ningún sitio */}
        <Route path="/inscripcion-gala" element={<GalaRegistration />} />

        {/* Gestión de inscripciones a la Gala — solo equipo NovaGob */}
        <Route
          path="/manage-gala"
          element={user ? <ManageGalaRegistrations /> : <Navigate to="/auth" />}
        />

        {/* Landing pública en la raíz del dominio (premios.novagob.org/) */}
        <Route path="/" element={<Landing />} />

        {/* /inicio se mantiene como alias por retrocompatibilidad con
            enlaces existentes; redirige a la raíz. */}
        <Route path="/inicio" element={<Navigate to="/" replace />} />

        {/* Cualquier otra ruta desconocida vuelve a la raíz (landing). */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </div>
  );
};

export default App;
